import { Component, OnInit, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { UserAdministratedBalances, BalanceRechargeSelectionData, EDENRED_BALANCE_TO_SLUG, EdenredBalanceId } from 'src/app/interfaces';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { AmipassRechargeModalComponent } from '../amipass-recharge-modal/amipass-recharge-modal.component';
import { EdenredRechargeModalComponent } from '../edenred-recharge-modal/edenred-recharge-modal.component';
import { isEdenredRechargeActive } from 'src/app/services/edenred-availability';

/**
 * Opens the balance recharge flow for the given methods. Routes straight to the specific
 * recharge dialog when only one method is available, otherwise opens the selection dialog.
 *
 * This decision is made before any dialog opens (rather than inside the selection dialog's
 * `ngOnInit`, closing itself and opening the real one) because Material's dialog close/open
 * animations are asynchronous: opening a replacement dialog before the first one finishes
 * closing puts both on screen at once, briefly showing the selection list underneath the
 * dialog that replaces it.
 */
export function openBalanceRecharge(
  dialog: MatDialog,
  analytics: AnalyticsService,
  data: BalanceRechargeSelectionData
): void {
  if (data.availableMethods.length === 1) {
    const method = data.availableMethods[0];
    analytics.logEvent("auto_select_recharge_method", {
      method,
      origin: data.origin,
      storeId: data.storeId
    });
    openRechargeMethodDialog(dialog, method, data.storeId);
    return;
  }

  dialog.open(BalanceRechargeSelectionComponent, { data });
}

/**
 * Opens the recharge dialog for a specific method.
 */
function openRechargeMethodDialog(
  dialog: MatDialog,
  method: UserAdministratedBalances,
  storeId?: string
): void {
  switch (method) {
    case UserAdministratedBalances.AMIPASS:
      dialog.open(AmipassRechargeModalComponent, {
        data: storeId
      });
      break;
    case UserAdministratedBalances.EDENRED:
    case UserAdministratedBalances.EDENRED_JUNAEB:
      // Both callers already strip Edenred from `availableMethods` while the flow is off, but
      // this is the last gate before the modal, so a future caller cannot reach the feature by
      // passing its own list.
      if (!isEdenredRechargeActive()) {
        console.warn('Edenred recharge is not enabled in this build');
        return;
      }
      dialog.open(EdenredRechargeModalComponent, {
        data: {
          balanceId: method,
          slug: EDENRED_BALANCE_TO_SLUG[method as EdenredBalanceId],
          storeId
        }
      });
      break;
    case UserAdministratedBalances.PLUXEE:
      // TODO: Implement Pluxee recharge
      console.warn('Pluxee recharge not yet implemented');
      break;
  }
}

@Component({
  selector: 'app-balance-recharge-selection',
  templateUrl: './balance-recharge-selection.component.html',
  styleUrls: ['./balance-recharge-selection.component.scss']
})
export class BalanceRechargeSelectionComponent implements OnInit {
  availableMethods: UserAdministratedBalances[];
  origin: string;
  storeId?: string;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: BalanceRechargeSelectionData,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<BalanceRechargeSelectionComponent>,
    private analytics: AnalyticsService
  ) {
    this.availableMethods = data.availableMethods;
    this.origin = data.origin;
    this.storeId = data.storeId;
  }

  close(): void {
    this.dialogRef.close();
  }

  ngOnInit(): void {
    this.analytics.logEvent("open_balance_recharge_selection", {
      origin: this.origin,
      methodCount: this.availableMethods.length,
      storeId: this.storeId
    });
  }

  /**
   * Handles method selection and routes to appropriate recharge flow
   * @param method The selected recharge method
   */
  onSelectMethod(method: UserAdministratedBalances): void {
    this.analytics.logEvent("select_recharge_method", {
      method,
      origin: this.origin,
      storeId: this.storeId
    });

    this.dialog.closeAll();
    openRechargeMethodDialog(this.dialog, method, this.storeId);
  }

  /**
   * Gets display name for a recharge method
   * @param method The recharge method
   * @returns Translation key for the method name
   */
  getMethodName(method: UserAdministratedBalances): string {
    return `components.balance_recharge_selection.methods.${method}`;
  }
}
